if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        // A configurator addon/upsell going out of stock can leave a stale "item
        // unavailable" error and disabled buttons after a failed add-to-cart. As
        // soon as the customer changes their selection, clear that state instead
        // of waiting for them to guess it'll resolve on the next submit attempt.
        document.addEventListener('configurator:selection-changed', (evt) => {
          if (evt.target.dataset.productFormId !== this.form.getAttribute('id')) return;
          // Never re-enable the buttons while an add-to-cart is in flight — the
          // configurator's on-demand accessory fetch can fire this mid-submit
          // (see whenAddonsReady in handleSubmit), and a live re-enable here
          // would let a second submit through.
          if (this._submitting) return;
          this.setErrorMsgState();
          this._getSubmitBtns().forEach((btn) => btn.removeAttribute('aria-disabled'));
        });
      }
    }

    /**
     * Returns every "Add to Cart" button tied to this form — both inside it and
     * external buttons linked via the form="<id>" attribute (e.g. a sticky panel).
     * @returns {Element[]}
     */
    _getSubmitBtns() {
      const formId = this.form.getAttribute('id');
      const externalSubmitBtns = Array.from(document.querySelectorAll(`[name="add"][form="${formId}"]`));
      return [...this.form.querySelectorAll('[name="add"]'), ...externalSubmitBtns];
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = evt.submitter || this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true' || this._submitting) return;
      this._submitting = true;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // this.form.id is unsafe here: the form contains <input name="id"> (the variant
      // field), and per the HTMLFormElement spec, named form controls shadow built-in
      // properties like .id — this.form.id returns that <input> ELEMENT, not the form's
      // id string. getAttribute('id') always returns the plain attribute string.
      const formId = this.form.getAttribute('id');

      // Disable ALL "Add to Cart" buttons (both in-form and external via form="...") until submission is complete.
      this.allSubmitBtns = this._getSubmitBtns();

      this.allSubmitBtns.forEach(btn => {
        btn.setAttribute('aria-disabled', 'true');
      });
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // A product-configurator (accessory slot / upsell picker) tied to this exact form may
      // have extra line items to add alongside the main variant in a single cart/add request.
      const configurator = document.querySelector(`product-configurator[data-product-form-id="${formId}"]`);
      if (configurator?.whenAddonsReady) {
        // The configurator may still be fetching the newly selected variant's
        // accessory data; reading getCartItems() before that lands would drop a
        // remembered accessory from the request. The buttons are already
        // aria-disabled above and _submitting blocks re-entry, so waiting here
        // can't let a second submit through. A sticky ATC button may have been
        // swapped in by _updateDynamicContent during the wait, though, so
        // re-take the button set and disable that one too.
        await configurator.whenAddonsReady();
        this.allSubmitBtns = this._getSubmitBtns();
        this.allSubmitBtns.forEach((btn) => btn.setAttribute('aria-disabled', 'true'));
      }
      const extraItems = configurator?.getCartItems ? configurator.getCartItems() : [];
      const mainItem = ProductForm.formDataToItem(formData);

      let fetchRequestOpts;
      if (extraItems.length) {
        fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [mainItem, ...extraItems],
            sections,
            sections_url: window.location.pathname
          })
        };
      } else {
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);

        fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        };
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        // The multi-item endpoint replies with { items: [...] }; the single-item
        // endpoint replies with the line item itself. Normalize so downstream code
        // can read variant_id/quantity the same way regardless of which path ran.
        const mainResultItem = data.items ? data.items[0] : data;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          const quickAddDrawer = this.closest('quick-add-drawer');
          const openCartDrawer = this.cartDrawer && theme.settings.afterAtc === 'drawer';

          // From the Quick Add drawer the cart drawer is the success feedback
          // (CartDrawer.open() handles closing Quick Add) — the inline "Added
          // to cart" message only shows when the cart drawer won't open.
          if (quickAddDrawer && !openCartDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === mainResultItem.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: mainResultItem.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === mainResultItem.quantity)
                ? itemInOldCart.quantity : mainResultItem.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: mainResultItem.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this._submitting = false;
        // Re-enable all 'Add to Cart' buttons — both the set disabled at the
        // start and whatever is live now, in case one was swapped in meanwhile.
        this.submitBtn.classList.add('is-success');
        new Set([...this.allSubmitBtns, ...this._getSubmitBtns()]).forEach(btn => {
          btn.removeAttribute('aria-disabled');
        });
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Converts the main product form's FormData into a cart/add.js item object,
     * for building a multi-item JSON request alongside configurator accessory/upsell items.
     * @param {FormData} formData - Form data from the product form.
     */
    static formDataToItem(formData) {
      const item = { properties: {} };
      for (const [key, value] of formData.entries()) {
        if (key === 'id') {
          item.id = parseInt(value, 10);
        } else if (key === 'quantity') {
          item.quantity = parseInt(value, 10) || 1;
        } else if (key === 'selling_plan') {
          item.selling_plan = value;
        } else if (key.startsWith('properties[') && key.endsWith(']')) {
          item.properties[key.slice('properties['.length, -1)] = value;
        }
      }
      if (!Object.keys(item.properties).length) delete item.properties;
      return item;
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
        
        // If submitted via external button (e.g. sticky panel), scroll to show the error.
        if (this.submitBtn && this.submitBtn.getAttribute('form')) {
          this.errorMsg.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
